import React, { useState, useEffect } from 'react';
import './AlertsPage.css';
import { ChevronDown, ArrowDownAZ } from 'lucide-react';
import { alertsData, alertsDataByTimeRange, Alert, AlertsData } from '../data/alertsData';

interface AlertsPageProps {
  initialTimeRange?: string;
}

const AlertsPage: React.FC<AlertsPageProps> = ({ initialTimeRange = 'Last 24h' }) => {
  const [activePhase, setActivePhase] = useState<number | null>(null);
  const [selectedTimeRange, setSelectedTimeRange] = useState<string>(initialTimeRange);
  const [data, setData] = useState<AlertsData>(alertsDataByTimeRange[initialTimeRange] || alertsData);
  // Update data when time range changes
  useEffect(() => {
    const newData = alertsDataByTimeRange[selectedTimeRange] || alertsData;
    setData(newData);
  }, [selectedTimeRange]);

  // When initialTimeRange prop changes, update the selected time range
  useEffect(() => {
    if (initialTimeRange && alertsDataByTimeRange[initialTimeRange]) {
      setSelectedTimeRange(initialTimeRange);
    }
  }, [initialTimeRange]);

  const handlePhaseClick = (phaseId: number) => {
    setActivePhase(phaseId);
  };

  return (
    <div className="alerts-page">
      <script
        type="text/javascript"
        dangerouslySetInnerHTML={{
          __html: `
            window._uxa = window._uxa || [];
            window._uxa.push(["trackDynamicVariable", { key: "alerts", value: "alerts_value" }]);
          `
        }}
      />
      <div className="alerts-filters">
        <div className="filter-group">
          <div className="filter-item">
            <span className="filter-label">Unit</span>
            <button className="filter-button">
              <span>All</span>
              <ChevronDown size={14} />
            </button>
          </div>
          <div className="filter-item">
            <span className="filter-label">Source</span>
            <button className="filter-button">
              <span>All</span>
              <ChevronDown size={14} />
            </button>
          </div>
          <div className="filter-item">
            <span className="filter-label">Management</span>
            <button className="filter-button">
              <span>All</span>
              <ChevronDown size={14} />
            </button>
          </div>
        </div>
      </div>

      <div className="alerts-overview">
        <div className="total-alerts-panel">
          <div className="panel-header">Total alerts</div>
          <div className="panel-count blue">{data.totalAlerts}</div>
        </div>

        {data.phases.map((phase) => (
          <div
            key={phase.id}
            className={`phase-panel phase-${phase.id}`}
            onClick={() => handlePhaseClick(phase.id)}
          >
            <div className="panel-header">
              {phase.name}
            </div>
            <div className={`panel-count phase-${phase.id}-count`}>
              {phase.count}
            </div>
          </div>
        ))}
      </div>

      {activePhase !== null && (
        <>
          <div className="phase-details-header">
            <h2>{data.phases[activePhase].name}</h2>
          </div>

          {data.phases[activePhase].alerts.length > 0 ? (
            <div className="alerts-table">
              <div className="table-header">
                <div className="table-cell type-col">
                  type <ArrowDownAZ size={14} />
                </div>
                <div className="table-cell name-col">
                  name <ArrowDownAZ size={14} />
                </div>
                <div className="table-cell team-col">
                  team <ArrowDownAZ size={14} />
                </div>
                <div className="table-cell line-col">
                  line <ArrowDownAZ size={14} />
                </div>
                <div className="table-cell unit-col">
                  unit <ArrowDownAZ size={14} />
                </div>
                <div className="table-cell region-col">
                  region <ArrowDownAZ size={14} />
                </div>
                <div className="table-cell runbook-col">
                  runbook <ChevronDown size={14} />
                </div>
              </div>

              {data.phases[activePhase].alerts.map((alert: Alert) => (
                <div key={alert.id} className="table-row">
                  <div className="table-cell type-col">{alert.type}</div>
                  <div className="table-cell name-col">{alert.name}</div>
                  <div className="table-cell team-col">{alert.team}</div>
                  <div className="table-cell line-col">{alert.line}</div>
                  <div className="table-cell unit-col">{alert.unit}</div>
                  <div className="table-cell region-col">{alert.region}</div>
                  <div className="table-cell runbook-col">
                    <a href={alert.runbook} target="_blank" rel="noopener noreferrer" className="runbook-link">
                      {alert.runbook}
                    </a>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-data-message">No data</div>
          )}
        </>
      )}
    </div>
  );
};

export default AlertsPage;
